/**
 * One node's card, with the SAME markup in the static picture (inside an SVG
 * foreignObject) and on the live canvas (inside a React Flow node), so the
 * page keeps its look when the canvas takes over from the picture.
 *
 * No handlers and no "use client": the picture renders on the server and in
 * the RSC template preview. The live canvas passes its interactive parts (the
 * node's links) in through `footer`.
 *
 * Every non-group card is drawn at its kind's FLOW_NODE_SIZE width and at
 * least that tall, the same box the auto layout and the picture's arrows use.
 * Longer content grows the card downwards; titles and descriptions are clamped.
 */
import type { CSSProperties, ReactElement, ReactNode } from "react";
import { IconCheck, IconLink, KindIcon } from "./icons.js";
import { FLOW_KIND_LABEL } from "./labels.js";
import { FLOW_NODE_SIZE, readText, type FlowMapLabels, type FlowNode, type FlowNodeMark } from "./model.js";

export interface FlowNodeCardProps {
  node: FlowNode;
  locale?: string;
  labels: FlowMapLabels;
  /** "picture": the static drawing (link count as a chip). "live": the canvas (checklist preview). */
  mode: "picture" | "live";
  selected?: boolean;
  /** The host's mark on this node (FlowMapProps.nodeMarks). */
  mark?: FlowNodeMark;
  /** Rendered last inside the card (the live canvas's interactive links). */
  footer?: ReactNode;
  /** Merged after the size styles, so a host can override them. */
  style?: CSSProperties;
}

/** How many checklist lines the live card previews. */
const LIVE_STEPS = 3;

function cardClass(node: FlowNode, selected: boolean | undefined, mark: FlowNodeMark | undefined): string {
  let c = `wa-fm-node wa-fm-node--${node.kind}`;
  if (node.tone) c += ` wa-fm-node--tone-${node.tone}`;
  if (mark) c += ` is-mark-${mark}`;
  if (selected) c += " is-selected";
  return c;
}

export function FlowNodeCard({ node, locale, labels, mode, selected, mark, footer, style }: FlowNodeCardProps): ReactElement {
  const title = readText(node.title, locale);
  const hasTitle = title.trim().length > 0;
  const titleClass = hasTitle ? "wa-fm-node__title" : "wa-fm-node__title is-empty";

  if (node.kind === "group") {
    const size = node.size ?? FLOW_NODE_SIZE.group;
    return (
      <div className={cardClass(node, selected, mark)} style={{ width: size.width, height: size.height, ...style }} data-kind="group">
        <div className="wa-fm-node__tab">
          <KindIcon kind="group" size={12} />
          <span className={titleClass}>{hasTitle ? title : labels.kindGroup}</span>
        </div>
        {footer}
      </div>
    );
  }

  const box = FLOW_NODE_SIZE[node.kind] ?? FLOW_NODE_SIZE.step;
  // Start and end are pills whose title is the whole card.
  const pill = node.kind === "start" || node.kind === "end";
  const badge = !pill && node.kind !== "note";
  const description = pill ? "" : readText(node.description, locale);
  const steps = pill ? [] : (node.steps ?? []);
  const done = steps.filter((s) => s.done).length;
  const linkCount = pill ? 0 : (node.links?.length ?? 0);
  const showLinkCount = mode === "picture" && linkCount > 0;

  return (
    <div className={cardClass(node, selected, mark)} style={{ width: box.width, minHeight: box.height, ...style }} data-kind={node.kind}>
      {badge ? (
        <div className="wa-fm-node__kind">
          <KindIcon kind={node.kind} size={12} />
          <span>{labels[FLOW_KIND_LABEL[node.kind]]}</span>
        </div>
      ) : null}
      <div className={titleClass}>{hasTitle ? title : labels.untitled}</div>
      {description.trim() ? <div className="wa-fm-node__desc">{description}</div> : null}
      {mode === "live" && steps.length > 0 ? (
        <ul className="wa-fm-node__steps">
          {steps.slice(0, LIVE_STEPS).map((s) => (
            <li key={s.id} className={s.done ? "is-done" : undefined}>
              <span className="wa-fm-node__check" aria-hidden="true">
                {s.done ? <IconCheck size={10} /> : null}
              </span>
              <span>{readText(s.text, locale)}</span>
            </li>
          ))}
          {steps.length > LIVE_STEPS ? <li className="wa-fm-node__more">+{steps.length - LIVE_STEPS}</li> : null}
        </ul>
      ) : null}
      {steps.length > 0 || showLinkCount ? (
        <div className="wa-fm-node__meta">
          {steps.length > 0 ? (
            <>
              <span className="wa-fm-node__progress" aria-hidden="true">
                <span style={{ width: `${Math.round((done / steps.length) * 100)}%` }} />
              </span>
              <span className="wa-fm-node__count">{labels.stepsProgress(done, steps.length)}</span>
            </>
          ) : null}
          {showLinkCount ? (
            <span className="wa-fm-node__count" title={labels.links}>
              <IconLink size={12} />
              {linkCount}
            </span>
          ) : null}
        </div>
      ) : null}
      {footer}
    </div>
  );
}
